import { useState, useEffect, useCallback } from 'react';
import { useMedplum, useSubscription } from '@medplum/react';
import {
  DiagnosticReport,
  ServiceRequest,
  Patient,
  Specimen,
} from '@medplum/fhirtypes';
import { notifications } from '@mantine/notifications';

// -----------------------------------------------------------------------------
export const useDiagnosticReport = (reportId?: string, orderId?: string) => {
  const medplum = useMedplum();
  const [loading, setLoading] = useState(false);
  const [diagnosticReport, setDiagnosticReport] =
    useState<DiagnosticReport | null>(null);

  const [serviceRequest, setServiceRequest] = useState<ServiceRequest | null>(
    null
  );

  const [patient, setPatient] = useState<Patient | null>(null);
  const [specimens, setSpecimens] = useState<Specimen[]>([]);

  // ---------------------------------------------------------------------------
  const loadData = useCallback(async () => {
    if (!reportId || !orderId) {
      return;
    }

    setLoading(true);
    try {
      // Load DiagnosticReport
      const report = await medplum.readResource('DiagnosticReport', reportId);
      setDiagnosticReport(report);

      // Load ServiceRequest
      const serviceRequestRef = report.basedOn?.[0]?.reference;
      if (serviceRequestRef) {
        const serviceReqId = serviceRequestRef.replace('ServiceRequest/', '');
        const serviceReq = await medplum.readResource(
          'ServiceRequest',
          serviceReqId
        );
        setServiceRequest(serviceReq);

        // Load Patient
        if (serviceReq.subject?.reference) {
          const patientId = serviceReq.subject.reference.replace(
            'Patient/',
            ''
          );

          const pat = await medplum.readResource('Patient', patientId);
          setPatient(pat);
        }
      }

      // Load Specimens if referenced
      if (report.specimen) {
        const specimenPromises = report.specimen
          .filter((ref) => ref.reference?.startsWith('Specimen/'))
          .map((ref) => {
            const specimenId = ref.reference?.replace('Specimen/', '') || '';
            return medplum.readResource('Specimen', specimenId);
          });

        const loadedSpecimens = await Promise.all(specimenPromises);
        setSpecimens(loadedSpecimens);
      }
    } catch (error: any) {
      notifications.show({
        title: 'Error Loading Report',
        message: error.message || 'Failed to load diagnostic report',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  }, [reportId, orderId, medplum]);

  // ---------------------------------------------------------------------------
  useEffect(() => {
    loadData();
  }, [loadData]);

  // ---------------------------------------------------------------------------
  useSubscription(reportId ? `DiagnosticReport/${reportId}` : '', (bundle) => {
    const updatedReport = bundle.entry?.find(
      (dr) => dr.resource?.resourceType === 'DiagnosticReport'
    )?.resource as DiagnosticReport | undefined;

    if (!updatedReport) {
      return;
    }
    // Update the diagnostic report in state
    setDiagnosticReport(updatedReport);
  });

  // ---------------------------------------------------------------------------
  return {
    loading,
    diagnosticReport,
    serviceRequest,
    patient,
    specimens,
    refetch: loadData,
    setDiagnosticReport,
  };
};
